import React from 'react';
import styled from 'styled-components/macro';
import { Body, Button, Dropdown, Heading, Menu } from '@cognite/cogs.js';
import { VerticalDivider } from 'components/Dividers/Dividers';

type ContentHeaderProps = {
  collection: {
    name: string;
    schemaFiltersCount: number;
    rulesCount: number;
  };
  showCollectionInfo: boolean;
  onToggleInfo: () => void;
  onCreateSchemaFilter: () => void;
  onCreateRule: () => void;
  onEditCollection: () => void;
  onDeleteCollection: () => void;
  onGenerateReport: () => void;
  isGeneratingReport: boolean;
  canGenerateReport?: boolean;
};

export const ContentHeader = ({
  collection,
  showCollectionInfo,
  onToggleInfo,
  onCreateSchemaFilter,
  onCreateRule,
  onEditCollection,
  onDeleteCollection,
  onGenerateReport,
  isGeneratingReport,
  canGenerateReport = true,
}: ContentHeaderProps) => {
  return (
    <StyledRowHeaderSmall>
      <StyledCol>
        <StyledInlineFlex>
          <Heading level={4}>{collection.name}</Heading>
          <StyledBodyGroup size="medium">
            <StyledSpanBold>{collection.schemaFiltersCount}</StyledSpanBold>{' '}
            schema filters
            <VerticalDivider />
            <StyledSpanBold>{collection.rulesCount}</StyledSpanBold> comparison
            rules
          </StyledBodyGroup>
        </StyledInlineFlex>
      </StyledCol>
      <StyledColButtons>
        <Button
          icon="Play"
          type="secondary"
          loading={isGeneratingReport}
          disabled={!canGenerateReport}
          onClick={onGenerateReport}
        >
          Generate report
        </Button>

        <Dropdown
          content={
            <Menu>
              <Menu.Item onClick={onCreateSchemaFilter}>
                <div>New schema filter</div>
              </Menu.Item>
              <Menu.Item onClick={onCreateRule}>
                <div>New comparison rule</div>
              </Menu.Item>
            </Menu>
          }
        >
          <Button icon="Add" type="primary">
            Create
          </Button>
        </Dropdown>

        <Dropdown
          content={
            <Menu>
              <Menu.Item
                onClick={onEditCollection}
                aria-label="Edit collection"
                icon="Edit"
                iconPlacement="left"
              >
                <span>Edit collection</span>
              </Menu.Item>

              <Menu.Item
                onClick={onDeleteCollection}
                aria-label="Delete collection"
                icon="Delete"
                iconPlacement="left"
                destructive
              >
                <span>Delete collection</span>
              </Menu.Item>
            </Menu>
          }
        >
          <Button
            icon="EllipsisVertical"
            type="ghost"
            aria-label="Collection options"
          />
        </Dropdown>

        <Button
          icon={showCollectionInfo ? 'ChevronUp' : 'ChevronDown'}
          type="ghost"
          aria-label="Expand"
          onClick={onToggleInfo}
        />
      </StyledColButtons>
    </StyledRowHeaderSmall>
  );
};

const StyledRowHeaderSmall = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0;
  margin-top: 0;
  width: 100%;
  padding: 1rem 2rem;
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
  background: var(--cogs-decorative--grayscale--white);
`;

const StyledCol = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledColButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StyledInlineFlex = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 1rem;
`;

const StyledBodyGroup = styled(Body)`
  display: inline-flex;
  gap: 0.5rem;
`;

const StyledSpanBold = styled.span`
  font-weight: 700;
`;
